/**
 * pv-a11y-disclosures.js — go-live a11y enhancements for non-native disclosure controls.
 * s289 Pattern C-rest. Sibling of pv-a11y-overlays.js. Self-contained, dependency-free, idempotent.
 *
 * Ella uses bare <h2>/<div> (and a disabled-link <a>) as collapsible / disclosure buttons,
 * toggled by jQuery/vendor CLICK handlers with NO role/tabindex/keyboard/aria-expanded.
 * Keyboard users cannot reach or operate them, and screen readers never announce open/closed
 * state. This progressively enhances them WITHOUT editing the theme's existing click handlers:
 *   - role="button" + tabindex="0"   → Tab reaches them, SR announces "button"
 *   - keydown Enter/Space → .click()  → reuses the existing click handler (no double-fire:
 *                                        <h2>/<div> have no native click activation)
 *   - aria-expanded synced by OBSERVING the state class the theme toggles (additive, no
 *     change to the theme's own open/close paths)
 *
 * Enhanced controls (live-verified s289; state class → aria-expanded source):
 *   C2 filter heading   .sidebarBlock-headingWrapper .sidebarBlock-heading   is-clicked (self)        [all viewports — sidebar/filter drawer]
 *   C3 filter show-more [data-show-more-btn]                                  show-more (.sidebarBlock) [all viewports]
 *   C4 footer accordion .footer-1 [data-toggle-column-footer]                is-clicked (self)        [mobile ≤767 only — desktop columns are always open]
 *   C5 bottom-bar action [data-cart-sidebar]/[data-search-mobile]/[data-open-auth-sidebar]/[data-bar-content]
 *                       (role="button" set in Liquid for action+content blocks) show (content panel) [keyboard wiring; aria-expanded for content]
 *   LM article load-more #article-page-load-btn (role/tabindex/aria-label set in Liquid; cloned into the
 *                       DOM from a <template> at runtime) — DELEGATED keydown only           [s290, search page; render-verified]
 *
 *   MM mega-menu / dropdown mobile submenu expanders (.menu_mobile_link / .menu_mobile_link_2 in the
 *                       hamburger drawer clone) — aria-expanded synced to the theme's is-open state
 *                       [s293; drawer populated by the B2 hamburger fix in theme.js, real-phone validated]
 */
(function () {
  'use strict';

  var observers = new WeakMap();

  function onKeyActivate(e) {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      e.currentTarget.click();
    }
  }

  // make a non-native element behave/announce as a button; reuse the theme's existing click handler
  function asButton(el) {
    if (el.dataset.pvA11yBtn) return;
    el.dataset.pvA11yBtn = '1';
    if (el.getAttribute('role') !== 'button') el.setAttribute('role', 'button');
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
    el.addEventListener('keydown', onKeyActivate);
  }

  // undo asButton (used when a mobile-only control crosses back to desktop)
  function unButton(el) {
    if (!el.dataset.pvA11yBtn) return;
    delete el.dataset.pvA11yBtn;
    el.removeEventListener('keydown', onKeyActivate);
    if (el.getAttribute('role') === 'button') el.removeAttribute('role');
    el.removeAttribute('tabindex');
    el.removeAttribute('aria-expanded');
    disconnect(el);
  }

  // element already has correct role/tabindex (set in Liquid) — just wire Enter/Space
  function keyboardOnly(el) {
    if (el.dataset.pvA11yKb) return;
    el.dataset.pvA11yKb = '1';
    el.addEventListener('keydown', onKeyActivate);
  }

  // keep aria-expanded on `btn` synced to whether `watch` currently has class `cls`
  function syncExpanded(btn, watch, cls) {
    if (!btn || !watch) return;
    var apply = function () {
      btn.setAttribute('aria-expanded', watch.classList.contains(cls) ? 'true' : 'false');
    };
    apply();
    if (observers.has(btn)) return;
    var o = new MutationObserver(apply);
    o.observe(watch, { attributes: true, attributeFilter: ['class'] });
    observers.set(btn, o);
  }

  function disconnect(el) {
    var o = observers.get(el);
    if (o) { o.disconnect(); observers.delete(el); }
  }

  // ---- always-on (interactive on all viewports) ----
  function initAlways() {
    // C2 — filter collapsible headings
    document.querySelectorAll('.sidebarBlock-headingWrapper .sidebarBlock-heading').forEach(function (h) {
      asButton(h);
      syncExpanded(h, h, 'is-clicked');
    });

    // C3 — filter show-more toggles
    document.querySelectorAll('[data-show-more-btn]').forEach(function (b) {
      asButton(b);
      var block = b.closest('.sidebarBlock');
      if (block) syncExpanded(b, block, 'show-more');
    });

    // C5 — bottom-bar action controls (role="button" set in Liquid; href-less / JS-actionable only).
    // cart/search/account open overlays (theme.js); data-bar-content toggles an inline panel
    // (handler is inline in halo-toolbar-mobile.liquid). All are click-bound → keydown reuses them.
    document.querySelectorAll(
      '#shopify-section-halo-toolbar-mobile [data-cart-sidebar],' +
      '#shopify-section-halo-toolbar-mobile [data-search-mobile],' +
      '#shopify-section-halo-toolbar-mobile [data-open-auth-sidebar],' +
      '#shopify-section-halo-toolbar-mobile [data-bar-content]'
    ).forEach(function (a) {
      if (a.getAttribute('role') !== 'button' || a.getAttribute('href')) return;
      keyboardOnly(a);
      // content trigger also exposes expanded state — its panel is the next sibling, toggling 'show'
      if (a.hasAttribute('data-bar-content') && a.nextElementSibling) {
        syncExpanded(a, a.nextElementSibling, 'show');
      }
    });
  }

  // LM — article load-more (#article-page-load-btn). It is cloned into the DOM from a <template> at
  // runtime (halo.search-by-all-types.js), so a direct listener would race the clone. role/tabindex/
  // aria-label are set in Liquid (carried through the clone); only keyboard activation needs wiring,
  // done via ONE delegated keydown (timing-independent). No double-fire: the <a> has no href, so
  // there is no native Enter activation to compound with .click().
  var loadMoreWired = false;
  function initLoadMore() {
    if (loadMoreWired) return;
    loadMoreWired = true;
    document.addEventListener('keydown', function (e) {
      var t = e.target;
      if (!t || t.id !== 'article-page-load-btn') return;
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        t.click();
      }
    });
  }

  // ---- mobile-only (≤767px) ----
  var mq = window.matchMedia('(max-width: 767px)');

  function applyMobile() {
    var on = mq.matches;

    // C4 — footer accordion (collapse behavior exists only on mobile; desktop columns stay open)
    document.querySelectorAll('.footer-1 [data-toggle-column-footer]').forEach(function (h) {
      if (on) { asButton(h); syncExpanded(h, h, 'is-clicked'); }
      else { unButton(h); }
    });

    // MM (mega-menu mobile submenu expanders) is handled by initMobileMenu(), not here — the drawer clone
    // is rebuilt by the B2 hamburger fix on every open (childList-driven, not mq-driven). See initMobileMenu().
  }

  // Re-run the (idempotent) enhancement whenever a dynamic region's children change.
  // Active dynamic path: AJAX filtering replaces .js-filter innerHTML inside <collection-filters-form>,
  // which discards the C2/C3 role/tabindex/aria-expanded/keydown on the replaced facet markup.
  function observeContainer(el, fn) {
    if (!el || el.dataset.pvA11yWatched) return;
    el.dataset.pvA11yWatched = '1';
    var t;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(fn, 50); // debounce bursty innerHTML mutations
    }).observe(el, { childList: true, subtree: true });
  }

  function initDynamicWatchers() {
    // C2/C3 — AJAX filter replacement re-renders facet markup
    document.querySelectorAll('collection-filters-form').forEach(function (form) {
      observeContainer(form, initAlways);
    });
  }

  // ---- MM — mega-menu / dropdown mobile submenu expanders (B2 drawer clone only) ----
  // PV MM (mobile-menu aria-expanded, s293): the B2 hamburger fix (theme.js, s292; real-phone validated
  // s293) clones the desktop nav (#HeaderNavigation .header__inline-menu) into the drawer
  // (#navigation-mobile .site-nav-mobile.nav) on every hamburger open (clear-first rebuild). The clone's
  // submenu toggles are native <a> already Enter-operable (Enter→click→theme delegated handler), so the
  // only gap is aria-expanded. The theme's own handlers set the state class 'is-open' on:
  //   lv1 .menu_mobile_link    → $target.parent()          (theme.js menuSidebarMobileToggle)
  //   lv2 .menu_mobile_link_2  → $target.parent().parent()
  // We mirror those exact elements (parentElement / parentElement.parentElement — no depth guessing) and
  // only sync aria-expanded. Scope is the drawer clone ONLY (#navigation-mobile), never the desktop
  // originals in #HeaderNavigation, so desktop hover-open is never mislabelled (separate node set, no
  // mq-gating). Re-applied on every open because B2 rebuilds the clone fresh (childList → observeContainer).
  var mmEnhanced = []; // toggles we attached aria-expanded observers to, for deterministic teardown
  function initMobileMenu() {
    var drawer = document.querySelector('#navigation-mobile .site-nav-mobile.nav');
    if (!drawer) return;
    // B2 clears + re-clones the drawer on every open, so the previous clone's toggle nodes are now
    // detached. Disconnect their observers deterministically (don't rely on GC of the WeakMap island).
    mmEnhanced.forEach(disconnect);
    mmEnhanced.length = 0;
    // lv1 toggles: .menu_mobile_link that expands a submenu (theme skips .list-menu__item--end = direct link)
    drawer.querySelectorAll('.menu_mobile_link:not(.list-menu__item--end)').forEach(function (a) {
      if (a.parentElement) { syncExpanded(a, a.parentElement, 'is-open'); mmEnhanced.push(a); }
    });
    // lv2 toggles: .menu_mobile_link_2 (theme adds is-open to parent().parent())
    drawer.querySelectorAll('.menu_mobile_link_2').forEach(function (a) {
      var watch = a.parentElement && a.parentElement.parentElement;
      if (watch) { syncExpanded(a, watch, 'is-open'); mmEnhanced.push(a); }
    });
  }

  function init() {
    initAlways();
    initLoadMore();
    applyMobile();
    initDynamicWatchers();
    initMobileMenu(); // MM — enhance drawer clone now (in case already populated)
    var pvDrawer = document.querySelector('#navigation-mobile .site-nav-mobile.nav');
    if (pvDrawer) observeContainer(pvDrawer, initMobileMenu); // re-run each B2 rebuild (childList)
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  if (mq.addEventListener) mq.addEventListener('change', applyMobile);
  else if (mq.addListener) mq.addListener(applyMobile);
})();
